import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Coffee, Sparkles, ExternalLink, RefreshCw, Loader2, AlertTriangle, Target } from 'lucide-react';
import { toast } from 'sonner';

const STATUT_LABELS = {
  interesse: 'Intéressé', en_negociation: 'En négociation',
  rdv_obtenu: 'RDV obtenu', rappel_demande: 'Rappel',
};

export default function DailyCoachWidget({ onProspectClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const load = async (force = false) => {
    if (force) setRegenerating(true); else setLoading(true);
    try {
      const { data } = await api.get(`/ai/coach-daily${force ? '?force=true' : ''}`);
      setData(data);
    } catch (e) {
      toast.error("Erreur chargement coach");
    } finally {
      setLoading(false); setRegenerating(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-[#F97316] to-[#EA580C] border-0 text-white" data-testid="coach-widget-loading">
        <CardContent className="p-4 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Ton coach prépare ton brief matinal…</span>
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.briefs || data.briefs.length === 0) {
    return (
      <Card className="bg-gradient-to-br from-[#F97316] to-[#EA580C] border-0 text-white" data-testid="coach-widget-empty">
        <CardContent className="p-4 flex items-center gap-3">
          <Coffee className="w-5 h-5" />
          <div className="flex-1">
            <div className="font-semibold text-sm">☕ Bonne nouvelle, tu as la journée libre !</div>
            <div className="text-xs opacity-90 mt-0.5">{data?.message || "Aucun prospect chaud à traiter ce matin. Attaque les nouveaux !"}</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-[#F97316] to-[#EA580C] border-0 text-white overflow-hidden" data-testid="coach-widget">
      <CardContent className="p-0">
        <div className="p-4 flex items-center justify-between gap-3 border-b border-white/20">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
              <Coffee className="w-5 h-5" />
            </div>
            <div>
              <div className="font-bold text-sm flex items-center gap-1.5">
                ☕ Ton coach du jour
                <Sparkles className="w-3.5 h-3.5 opacity-80" />
              </div>
              <div className="text-[11px] opacity-90">
                Brief IA de {data.briefs.length === 1 ? 'ton prospect le plus chaud' : `tes ${data.briefs.length} prospects les plus chauds`} — {new Date(data.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
              </div>
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => load(true)}
            disabled={regenerating}
            className="text-white hover:bg-white/20 h-7"
            data-testid="coach-refresh-btn"
            title="Regénérer le brief"
          >
            {regenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>

        <div className="p-3 space-y-2 bg-white/95 text-[#18181B]">
          {data.briefs.map((b, i) => (
            <div
              key={b.prospect_id}
              className={`flex items-start gap-2 p-2.5 rounded-lg border border-[#E5E7EB] transition-colors ${onProspectClick ? 'hover:border-[#F97316]/40 hover:bg-[#FFF7ED] cursor-pointer' : ''}`}
              onClick={() => onProspectClick?.(b.prospect_id)}
              data-testid={`coach-brief-${i}`}
            >
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{ background: i === 0 ? '#F97316' : i === 1 ? '#FB923C' : '#FDBA74', color: 'white' }}
              >
                {b.priorite || (i + 1)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap mb-1">
                  <span className="font-semibold text-sm truncate">{b.nom}</span>
                  <Badge variant="outline" className="text-[9px] h-4 border-[#F97316] text-[#F97316]">
                    {STATUT_LABELS[b.statut] || b.statut}
                  </Badge>
                  <span className="text-[10px] text-[#71717A]">• {b.ville}</span>
                  {b.prenom_dirigeant && <span className="text-[10px] text-[#71717A]">• {b.prenom_dirigeant} {b.nom_dirigeant}</span>}
                </div>
                <div className="flex items-start gap-1.5 text-xs mb-1">
                  <Target className="w-3 h-3 text-[#10B981] flex-shrink-0 mt-0.5" />
                  <span className="text-[#18181B]"><strong>Angle :</strong> {b.angle}</span>
                </div>
                <div className="flex items-start gap-1.5 text-xs">
                  <AlertTriangle className="w-3 h-3 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                  <span className="text-[#71717A]"><strong>Risque :</strong> {b.risque}</span>
                </div>
              </div>
              {b.telephone && b.telephone !== '?' && (
                <button
                  onClick={e => { e.stopPropagation(); onProspectClick?.(b.prospect_id); }}
                  className="flex items-center gap-1 h-8 px-2.5 rounded-full bg-[#F97316] hover:bg-[#EA580C] text-white text-xs font-medium flex-shrink-0 transition-colors"
                  title="Ouvrir la fiche prospect"
                  data-testid={`coach-open-${i}`}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>Ouvrir</span>
                </button>
              )}
            </div>
          ))}
          {data.source === 'fallback' && (
            <div className="text-[10px] text-[#71717A] text-center pt-1">
              ⚠️ Brief simplifié (IA indisponible temporairement)
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
